import { Injectable, computed, inject, signal } from '@angular/core';

export type SpeechLocale = 'it-IT' | 'en-US';
export type SpeechMode = 'local' | 'device';
export type SpeechRouteKind = 'local-loading' | 'local' | 'device' | 'browser-fallback' | 'unavailable';
export type SpeechVoiceState = 'not-loaded' | 'cached' | 'loading' | 'ready' | 'failed';
export type SpeechCacheState = 'unknown' | 'missing' | 'present' | 'validated';

export interface SpeechVoiceDiagnostic {
  readonly locale: SpeechLocale;
  readonly name: string;
  readonly state: SpeechVoiceState;
  readonly cacheState: SpeechCacheState;
  readonly active: boolean;
  readonly progress: number | null;
  readonly message: string;
  readonly lastRoute: SpeechRouteKind | null;
  readonly cachedParts: number | null;
  readonly networkParts: number | null;
  readonly packSizeBytes: number | null;
  readonly model: string | null;
  readonly buildId: string | null;
  readonly lastError: string | null;
}

export interface SpeechEngine {
  readonly id: string;
  isAvailable(locale?: SpeechLocale): boolean;
  speak(text: string, locale?: SpeechLocale): void;
  cancel(): void;
}

interface LocalTtsMetrics {
  readonly cachedParts?: number;
  readonly networkParts?: number;
}

interface LocalTtsWorkerMessage {
  readonly type: 'ready' | 'result' | 'error' | 'status' | 'progress';
  readonly text?: string;
  readonly samples?: Float32Array;
  readonly sampleRate?: number;
  readonly requestId?: number;
  readonly fatal?: boolean;
  readonly message?: string;
  readonly locale?: SpeechLocale;
  readonly value?: number;
  readonly packSizeBytes?: number;
  readonly model?: string;
  readonly buildId?: string;
  readonly metrics?: LocalTtsMetrics;
}

interface VoiceAssetMeta {
  readonly path: string;
  readonly sha256: string;
  readonly sizeBytes: number;
}

interface VoicePackManifest {
  readonly schemaVersion: number;
  readonly id: string;
  readonly locale: SpeechLocale;
  readonly buildId: string;
  readonly parts: readonly VoiceAssetMeta[];
  readonly runtime?: {
    readonly wasm?: VoiceAssetMeta;
    readonly mainJs?: VoiceAssetMeta;
    readonly wrapperJs?: VoiceAssetMeta;
  };
}

interface PrepareWaiter {
  readonly locale: SpeechLocale;
  readonly resolve: () => void;
  readonly reject: (error: Error) => void;
  readonly timeoutId: ReturnType<typeof setTimeout>;
}

const BOOTSTRAP_CACHE = 'learning-garden-tts-bootstrap-v2';
const BUILD_CACHE_PREFIX = 'learning-garden-tts-';
const SPEECH_MODE_STORAGE_KEY = 'learning-garden-speech-mode-v1';

function readSpeechMode(): SpeechMode {
  if (typeof window === 'undefined') {
    return 'local';
  }
  try {
    return window.localStorage.getItem(SPEECH_MODE_STORAGE_KEY) === 'device' ? 'device' : 'local';
  } catch {
    return 'local';
  }
}

function persistSpeechMode(mode: SpeechMode): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(SPEECH_MODE_STORAGE_KEY, mode);
  } catch {
    // Speech preference is best-effort; inability to persist must not block audio.
  }
}

function voiceName(locale: SpeechLocale): string {
  return locale === 'en-US' ? 'Lessac · English' : 'Paola · Italiano';
}

function initialDiagnostic(locale: SpeechLocale): SpeechVoiceDiagnostic {
  return {
    locale,
    name: voiceName(locale),
    state: 'not-loaded',
    cacheState: 'unknown',
    active: false,
    progress: null,
    message: 'Stato non ancora verificato.',
    lastRoute: null,
    cachedParts: null,
    networkParts: null,
    packSizeBytes: null,
    model: null,
    buildId: null,
    lastError: null
  };
}

@Injectable({ providedIn: 'root' })
export class BrowserSpeechEngine implements SpeechEngine {
  readonly id = 'browser-speech-synthesis';

  isAvailable(locale: SpeechLocale = 'it-IT'): boolean {
    if (
      typeof window === 'undefined' ||
      !('speechSynthesis' in window) ||
      typeof SpeechSynthesisUtterance === 'undefined'
    ) {
      return false;
    }

    // Italian retains the historical browser fallback. English is stricter:
    // without an actual en-* voice we never pronounce English with the device
    // default voice.
    return locale === 'it-IT' || this.voiceFor(locale) !== null;
  }

  speak(text: string, locale: SpeechLocale = 'it-IT'): void {
    if (!this.isAvailable(locale)) {
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = locale;
    utterance.rate = 0.9;
    const matchingVoice = this.voiceFor(locale);
    if (matchingVoice) {
      utterance.voice = matchingVoice;
    }
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  }

  cancel(): void {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }

  private voiceFor(locale: SpeechLocale): SpeechSynthesisVoice | null {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      return null;
    }
    const language = locale.slice(0, 2).toLowerCase();
    return window.speechSynthesis
      .getVoices()
      .find((voice) => voice.lang.toLowerCase().startsWith(language)) ?? null;
  }
}

@Injectable({ providedIn: 'root' })
export class LocalTtsSpeechEngine implements SpeechEngine {
  readonly id = 'sherpa-onnx-local-tts';

  private readonly browserFallback = inject(BrowserSpeechEngine);
  private readonly diagnosticState = signal<Record<SpeechLocale, SpeechVoiceDiagnostic>>({
    'it-IT': initialDiagnostic('it-IT'),
    'en-US': initialDiagnostic('en-US')
  });

  readonly diagnostics = computed(() => [
    this.diagnosticState()['it-IT'],
    this.diagnosticState()['en-US']
  ] as const);

  private worker: Worker | null = null;
  private ready = false;
  private failed = false;
  private locale: SpeechLocale = 'it-IT';
  private requestSequence = 0;
  private activeRequestId = 0;
  private audioContext: AudioContext | null = null;
  private activeSource: AudioBufferSourceNode | null = null;
  private pendingSpeech: { text: string; locale: SpeechLocale } | null = null;
  private prepareWaiters: PrepareWaiter[] = [];

  warmUp(locale: SpeechLocale = 'it-IT'): void {
    this.ensureLocale(locale);
  }

  deactivate(): void {
    this.disposeWorker('TTS locale disattivato in favore della voce del dispositivo.');
  }

  markDeviceRoute(locale: SpeechLocale, available: boolean): void {
    this.setRoute(available ? 'device' : 'unavailable', locale);
  }

  ensureLocale(locale: SpeechLocale): void {
    if (this.worker && this.locale === locale && !this.failed) {
      return;
    }

    if (this.locale !== locale || this.failed) {
      this.disposeWorker();
      this.locale = locale;
      this.failed = false;
      this.ready = false;
    }

    if (this.worker) {
      return;
    }

    if (typeof window === 'undefined' || typeof Worker === 'undefined') {
      this.failed = true;
      this.updateDiagnostic(locale, {
        state: 'failed',
        active: false,
        message: 'Il browser non supporta il worker necessario al TTS locale.',
        lastError: 'Web Worker non disponibile.'
      });
      this.setRoute('unavailable', locale);
      return;
    }

    try {
      const directory = locale === 'en-US' ? 'tts-en' : 'tts';
      const workerUrl = new URL(`${directory}/tts-worker.js`, document.baseURI);
      this.updateDiagnostic(locale, {
        state: 'loading',
        active: true,
        progress: 0,
        message: `Preparo ${voiceName(locale)}…`,
        lastError: null
      });
      this.setRoute('local-loading', locale);
      this.worker = new Worker(workerUrl, { name: `learning-garden-local-tts-${locale}` });
      this.worker.onmessage = (event: MessageEvent<LocalTtsWorkerMessage>) => this.handleMessage(event.data);
      this.worker.onerror = (event) => {
        console.warn(`Local TTS worker failed for ${this.locale}.`, event.message);
        const pending = this.pendingSpeech;
        const failedLocale = this.locale;
        this.disableLocalEngine(event.message || 'Errore del worker TTS locale.');
        if (pending && this.browserFallback.isAvailable(pending.locale)) {
          this.setRoute('browser-fallback', pending.locale);
          this.browserFallback.speak(pending.text, pending.locale);
        } else {
          this.setRoute('unavailable', failedLocale);
        }
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.warn(`Local TTS could not start for ${this.locale}.`, error);
      this.disableLocalEngine(message);
    }
  }

  isAvailable(locale: SpeechLocale = this.locale): boolean {
    return locale === this.locale && this.ready && !this.failed && this.worker !== null;
  }

  async prepare(locale: SpeechLocale): Promise<void> {
    this.ensureLocale(locale);
    if (this.isAvailable(locale)) {
      return;
    }
    if (this.locale === locale && this.failed) {
      throw new Error(this.diagnosticState()[locale].lastError ?? 'TTS locale non disponibile.');
    }

    await new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.prepareWaiters = this.prepareWaiters.filter((waiter) => waiter.timeoutId !== timeoutId);
        reject(new Error(`Timeout durante la preparazione di ${voiceName(locale)}.`));
      }, 90000);
      this.prepareWaiters.push({ locale, resolve, reject, timeoutId });
    });
  }

  async forceDownload(locale: SpeechLocale): Promise<void> {
    if (this.locale === locale && (this.worker !== null || this.failed)) {
      this.disposeWorker();
      this.failed = false;
    }
    await this.clearVoiceCaches(locale);
    await this.prepare(locale);
  }

  async refreshDiagnostics(): Promise<void> {
    await Promise.all((['it-IT', 'en-US'] as const).map((locale) => this.inspectCachedVoice(locale)));
  }

  speak(text: string, locale: SpeechLocale = 'it-IT'): void {
    this.ensureLocale(locale);
    if (!this.isAvailable(locale) || !this.worker) {
      // Both supported locales are local-first. While the selected local voice
      // is loading, keep the latest prompt queued instead of immediately using
      // Android/browser speech. Device speech is reserved for an actual local
      // engine failure or a browser without local-worker support.
      if (this.worker && !this.failed) {
        this.pendingSpeech = { text, locale };
        this.setRoute('local-loading', locale);
        return;
      }

      if (this.browserFallback.isAvailable(locale)) {
        this.pendingSpeech = null;
        this.setRoute('browser-fallback', locale);
        this.browserFallback.speak(text, locale);
      } else {
        this.pendingSpeech = null;
        this.setRoute('unavailable', locale);
      }
      return;
    }

    this.pendingSpeech = null;
    this.setRoute('local', locale);
    this.prepareAudioContext();
    this.stopPlayback();
    const requestId = ++this.requestSequence;
    this.activeRequestId = requestId;
    this.worker.postMessage({ type: 'generate', text, requestId });
  }

  cancel(): void {
    this.pendingSpeech = null;
    this.activeRequestId = ++this.requestSequence;
    this.stopPlayback();
  }

  private handleMessage(message: LocalTtsWorkerMessage): void {
    if (message.type === 'status') {
      if (message.message) {
        this.updateDiagnostic(this.locale, {
          state: 'loading',
          active: true,
          message: message.message
        });
      }
      return;
    }

    if (message.type === 'progress') {
      this.updateDiagnostic(this.locale, {
        state: 'loading',
        active: true,
        progress: typeof message.value === 'number' ? message.value : null,
        message: message.message ?? `Preparo ${voiceName(this.locale)}…`
      });
      return;
    }

    if (message.type === 'ready') {
      if (message.locale && message.locale !== this.locale) {
        console.warn(`Ignoring TTS voice ${message.locale}; expected ${this.locale}.`);
        this.disableLocalEngine(`Voce ${message.locale} ricevuta mentre era richiesta ${this.locale}.`);
        return;
      }
      this.ready = true;
      this.failed = false;
      this.updateDiagnostic(this.locale, {
        state: 'ready',
        cacheState: 'validated',
        active: true,
        progress: 1,
        message: `${voiceName(this.locale)} pronta in locale.`,
        cachedParts: message.metrics?.cachedParts ?? null,
        networkParts: message.metrics?.networkParts ?? null,
        packSizeBytes: message.packSizeBytes ?? null,
        model: message.model ?? null,
        buildId: message.buildId ?? null,
        lastError: null
      });
      this.resolvePrepareWaiters(this.locale);
      const pending = this.pendingSpeech;
      if (pending && pending.locale === this.locale) {
        this.pendingSpeech = null;
        this.speak(pending.text, pending.locale);
      }
      return;
    }

    if (message.type === 'result') {
      if (
        message.requestId !== this.activeRequestId ||
        !message.samples ||
        !message.sampleRate ||
        !message.text
      ) {
        return;
      }

      void this.play(message.samples, message.sampleRate).catch((error) => {
        console.warn(`Local TTS audio playback failed for ${this.locale}.`, error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        this.updateDiagnostic(this.locale, { lastError: errorMessage });
        if (this.browserFallback.isAvailable(this.locale)) {
          this.setRoute('browser-fallback', this.locale);
          this.browserFallback.speak(message.text!, this.locale);
        } else {
          this.setRoute('unavailable', this.locale);
        }
      });
      return;
    }

    if (message.type === 'error') {
      console.warn(`Local TTS error for ${this.locale}.`, message.message);
      const errorMessage = message.message ?? 'Errore TTS locale.';
      const failedLocale = this.locale;
      const pending = message.fatal ? this.pendingSpeech : null;
      if (message.fatal) {
        this.disableLocalEngine(errorMessage);
      } else {
        this.updateDiagnostic(this.locale, { lastError: errorMessage, message: errorMessage });
      }
      if (
        message.text &&
        message.requestId === this.activeRequestId &&
        this.browserFallback.isAvailable(failedLocale)
      ) {
        this.setRoute('browser-fallback', failedLocale);
        this.browserFallback.speak(message.text, failedLocale);
      } else if (message.fatal && pending && this.browserFallback.isAvailable(pending.locale)) {
        this.setRoute('browser-fallback', pending.locale);
        this.browserFallback.speak(pending.text, pending.locale);
      } else if (message.fatal) {
        this.setRoute('unavailable', failedLocale);
      }
    }
  }

  private prepareAudioContext(): void {
    if (typeof AudioContext === 'undefined') {
      return;
    }

    this.audioContext ??= new AudioContext();
    if (this.audioContext.state === 'suspended') {
      void this.audioContext.resume().catch(() => undefined);
    }
  }

  private async play(samples: Float32Array, sampleRate: number): Promise<void> {
    this.prepareAudioContext();
    const context = this.audioContext;
    if (!context) {
      throw new Error('Web Audio is unavailable.');
    }
    if (context.state === 'suspended') {
      await context.resume();
    }

    const buffer = context.createBuffer(1, samples.length, sampleRate);
    const channelSamples = new Float32Array(samples);
    buffer.copyToChannel(channelSamples, 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.onended = () => {
      if (this.activeSource === source) {
        this.activeSource = null;
      }
    };
    this.activeSource = source;
    source.start();
  }

  private stopPlayback(): void {
    if (!this.activeSource) {
      return;
    }
    try {
      this.activeSource.stop();
    } catch {
      // The source may already have ended.
    }
    this.activeSource.disconnect();
    this.activeSource = null;
  }

  private disposeWorker(reason = `Preparazione di ${voiceName(this.locale)} interrotta dal cambio lingua.`): void {
    const previousLocale = this.locale;
    const previous = this.diagnosticState()[previousLocale];
    this.cancel();
    this.worker?.terminate();
    this.worker = null;
    this.ready = false;
    this.rejectPrepareWaiters(previousLocale, new Error(reason));
    if (previous.state === 'ready' || previous.cacheState === 'validated' || previous.cacheState === 'present') {
      this.updateDiagnostic(previousLocale, {
        state: 'cached',
        active: false,
        progress: null,
        message: `${voiceName(previousLocale)} disponibile in cache; non caricata in memoria.`
      });
    } else if (previous.state !== 'failed') {
      this.updateDiagnostic(previousLocale, {
        state: 'not-loaded',
        active: false,
        progress: null,
        message: `${voiceName(previousLocale)} non caricata in memoria.`
      });
    }
  }

  private disableLocalEngine(errorMessage: string): void {
    const failedLocale = this.locale;
    this.ready = false;
    this.failed = true;
    this.pendingSpeech = null;
    this.stopPlayback();
    this.worker?.terminate();
    this.worker = null;
    this.updateDiagnostic(failedLocale, {
      state: 'failed',
      active: false,
      progress: null,
      message: `TTS locale non disponibile: ${errorMessage}`,
      lastError: errorMessage
    });
    this.rejectPrepareWaiters(failedLocale, new Error(errorMessage));
    this.setRoute('unavailable', failedLocale);
  }

  private setRoute(kind: SpeechRouteKind, locale: SpeechLocale): void {
    this.updateDiagnostic(locale, { lastRoute: kind });
  }

  private updateDiagnostic(locale: SpeechLocale, patch: Partial<SpeechVoiceDiagnostic>): void {
    this.diagnosticState.update((current) => ({
      ...current,
      [locale]: { ...current[locale], ...patch }
    }));
  }

  private resolvePrepareWaiters(locale: SpeechLocale): void {
    const matching = this.prepareWaiters.filter((waiter) => waiter.locale === locale);
    this.prepareWaiters = this.prepareWaiters.filter((waiter) => waiter.locale !== locale);
    for (const waiter of matching) {
      clearTimeout(waiter.timeoutId);
      waiter.resolve();
    }
  }

  private rejectPrepareWaiters(locale: SpeechLocale, error: Error): void {
    const matching = this.prepareWaiters.filter((waiter) => waiter.locale === locale);
    this.prepareWaiters = this.prepareWaiters.filter((waiter) => waiter.locale !== locale);
    for (const waiter of matching) {
      clearTimeout(waiter.timeoutId);
      waiter.reject(error);
    }
  }

  private voiceDirectory(locale: SpeechLocale): string {
    return locale === 'en-US' ? 'tts-en' : 'tts';
  }

  private voicePackId(locale: SpeechLocale): string {
    return locale === 'en-US' ? 'learning-garden-en-us-tts' : 'learning-garden-it-tts';
  }

  private async clearVoiceCaches(locale: SpeechLocale): Promise<void> {
    if (typeof caches === 'undefined') {
      throw new Error('Cache Storage non disponibile su questo browser.');
    }

    const slug = this.voicePackId(locale).replace(/[^a-zA-Z0-9-]/g, '-');
    const prefix = `${BUILD_CACHE_PREFIX}${slug}-`;
    const keys = await caches.keys();
    await Promise.all(keys.filter((key) => key.startsWith(prefix)).map((key) => caches.delete(key)));
    this.updateDiagnostic(locale, {
      state: 'not-loaded',
      cacheState: 'missing',
      active: false,
      progress: null,
      cachedParts: null,
      networkParts: null,
      packSizeBytes: null,
      model: null,
      buildId: null,
      lastError: null,
      message: `${voiceName(locale)} rimossa dalla cache. Avvio un nuovo download verificato.`
    });
  }

  private async inspectCachedVoice(locale: SpeechLocale): Promise<void> {
    if (typeof window === 'undefined' || typeof caches === 'undefined' || typeof document === 'undefined') {
      return;
    }

    try {
      const baseUrl = new URL(`${this.voiceDirectory(locale)}/`, document.baseURI);
      const manifestRequest = new Request(new URL('voice-pack.json', baseUrl), {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      let manifestResponse: Response | undefined;
      try {
        const network = await fetch(manifestRequest);
        if (network.ok) {
          manifestResponse = network;
        }
      } catch {
        // Offline inspection falls back to the manifest cached by the worker.
      }

      if (!manifestResponse) {
        const bootstrapCache = await caches.open(BOOTSTRAP_CACHE);
        manifestResponse = await bootstrapCache.match(manifestRequest) ?? undefined;
      }

      if (!manifestResponse) {
        this.markCacheMissing(locale);
        return;
      }

      const manifest = await manifestResponse.json() as VoicePackManifest;
      if (!manifest.id || !manifest.buildId || manifest.locale !== locale) {
        this.markCacheMissing(locale);
        return;
      }

      const cacheSlug = manifest.id.replace(/[^a-zA-Z0-9-]/g, '-');
      const cache = await caches.open(`${BUILD_CACHE_PREFIX}${cacheSlug}-${manifest.buildId}`);
      const assets = [
        ...manifest.parts,
        manifest.runtime?.wasm,
        manifest.runtime?.mainJs,
        manifest.runtime?.wrapperJs
      ].filter((asset): asset is VoiceAssetMeta => Boolean(asset?.path));

      let complete = assets.length > 0;
      for (const asset of assets) {
        const assetUrl = new URL(asset.path, baseUrl);
        assetUrl.searchParams.set('v', manifest.buildId);
        if (!await cache.match(assetUrl.href)) {
          complete = false;
          break;
        }
      }

      const current = this.diagnosticState()[locale];
      if (complete) {
        if (current.state !== 'ready' && current.state !== 'loading') {
          this.updateDiagnostic(locale, {
            state: 'cached',
            cacheState: current.cacheState === 'validated' ? 'validated' : 'present',
            active: false,
            message: current.cacheState === 'validated'
              ? `${voiceName(locale)} verificata e disponibile offline.`
              : `${voiceName(locale)} presente in cache; sarà verificata al caricamento.`,
            buildId: manifest.buildId
          });
        }
      } else if (current.state !== 'ready' && current.state !== 'loading') {
        this.markCacheMissing(locale, manifest.buildId);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const current = this.diagnosticState()[locale];
      if (current.state !== 'ready' && current.state !== 'loading') {
        this.updateDiagnostic(locale, {
          cacheState: 'unknown',
          message: `Impossibile verificare la cache: ${message}`,
          lastError: message
        });
      }
    }
  }

  private markCacheMissing(locale: SpeechLocale, buildId: string | null = null): void {
    const current = this.diagnosticState()[locale];
    if (current.state === 'ready' || current.state === 'loading') {
      return;
    }
    this.updateDiagnostic(locale, {
      state: 'not-loaded',
      cacheState: 'missing',
      active: false,
      message: `${voiceName(locale)} non è ancora disponibile completamente offline.`,
      buildId
    });
  }
}

@Injectable({ providedIn: 'root' })
export class SpeechService {
  private readonly browserFallback = inject(BrowserSpeechEngine);
  private readonly localEngine = inject(LocalTtsSpeechEngine);
  private primaryEngine: SpeechEngine | null = this.localEngine;

  readonly mode = signal<SpeechMode>(readSpeechMode());
  readonly diagnostics = this.localEngine.diagnostics;

  constructor() {
    if (this.mode() === 'device') {
      this.primaryEngine = this.browserFallback;
    } else {
      // Paola is warmed on startup only when the local engine is selected.
      // Device mode deliberately avoids starting the heavy local worker.
      this.localEngine.warmUp('it-IT');
    }
    void this.localEngine.refreshDiagnostics();
  }

  setMode(mode: SpeechMode): void {
    if (this.mode() === mode) {
      return;
    }

    this.cancel();
    this.mode.set(mode);
    persistSpeechMode(mode);

    if (mode === 'device') {
      this.localEngine.deactivate();
      this.primaryEngine = this.browserFallback;
      return;
    }

    this.primaryEngine = this.localEngine;
    this.localEngine.warmUp('it-IT');
  }

  setPrimaryEngine(engine: SpeechEngine | null): void {
    this.primaryEngine?.cancel();
    this.primaryEngine = engine;
  }

  speak(text: string, locale: SpeechLocale = 'it-IT'): void {
    if (this.mode() === 'device') {
      const available = this.browserFallback.isAvailable(locale);
      this.localEngine.markDeviceRoute(locale, available);
      if (available) {
        this.browserFallback.speak(text, locale);
      }
      return;
    }

    if (this.primaryEngine === this.localEngine) {
      this.localEngine.speak(text, locale);
      return;
    }
    const engine = this.primaryEngine?.isAvailable(locale) ? this.primaryEngine : this.browserFallback;
    engine.speak(text, locale);
  }

  async prepareVoice(locale: SpeechLocale): Promise<void> {
    await this.localEngine.prepare(locale);
    await this.localEngine.refreshDiagnostics();
  }

  async forceDownloadVoice(locale: SpeechLocale): Promise<void> {
    await this.localEngine.forceDownload(locale);
    await this.localEngine.refreshDiagnostics();
  }

  async refreshDiagnostics(): Promise<void> {
    await this.localEngine.refreshDiagnostics();
  }

  async testVoice(locale: SpeechLocale): Promise<void> {
    const sample = locale === 'en-US'
      ? (this.mode() === 'device' ? 'Hello! This is your device voice.' : 'Hello! This is the local English voice.')
      : (this.mode() === 'device' ? 'Ciao! Questa è la voce del dispositivo.' : 'Ciao! Questa è la voce italiana locale.');

    if (this.mode() === 'device') {
      const available = this.browserFallback.isAvailable(locale);
      this.localEngine.markDeviceRoute(locale, available);
      if (!available) {
        throw new Error(`Voce del dispositivo non disponibile per ${locale}.`);
      }
      this.browserFallback.speak(sample, locale);
      return;
    }

    await this.localEngine.prepare(locale);
    this.localEngine.speak(sample, locale);
  }

  cancel(): void {
    this.primaryEngine?.cancel();
    this.localEngine.cancel();
    this.browserFallback.cancel();
  }
}
